import { useCallback, useState } from 'react'
import { Box, Button, Menu, MenuItem, Tooltip } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EventBusyIcon from '@mui/icons-material/EventBusy'
import EventIcon from '@mui/icons-material/Event'
import { useQueryClient } from '@tanstack/react-query'
import { appointment, UserCanBookAppointmentsV2RetrieveResponse } from '@fireflyhealth/api'
import { useSnackbar } from 'notistack'
import { EmptyState, Loader, SegmentedControl } from '~/shared/ui'
import {
  AppointmentTypeMapping,
  useAppointmentTypeGroups,
  LegacyVisitReason,
  VisitReason,
  type AppointmentTypeGroup,
  type ServerVisitReason,
} from './AppointmentService'
import { useFeatureSwitches } from '~/shared/hooks/useFeatureSwitch'
import { logger } from '~/shared/lib/logger'
import BookAppointmentModal from './BookAppointment/BookAppointmentModal'
import CancelAppointmentModal from './CancelAppointmentModal'
import ConfirmCancelApptModal from './ConfirmCancelApptModal'
import { AppointmentCard } from './AppointmentCard'
import { VISIT_REASON_TO_LABEL } from './AppointmentsTab'
import { type SymptomItem } from './BookAppointment/PrimaryConcern'
import {
  reasonToVisitReason,
  useGetCanUserBookAppointmentsV2,
  useGetPatientAppointmentCancelationsInfo,
  useGetPatientAppointmentsInfo,
  VisitTypeMapping,
  type Appointment,
  type AppointmentCancelation,
  type ApptTypes,
} from './PatientAppointmentsService'

type AppointmentTabTypes = 'appointments' | 'cancellations'

// One switch drives both sides of the cutover: the backend widens its type
// filters and this tab swaps its menu source from the eligibility booleans to
// the server's appointment type groups.
const SWITCH_SERVER_DRIVEN_APPOINTMENT_TYPES = 'appointment.crm_server_driven_appointment_types'

type AppointmentTypeEligibilities =
  UserCanBookAppointmentsV2RetrieveResponse['appointment_type_eligibilities']

// Returns the appointment types a member can be booked for.
const bookableVisitTypes = (eligibilities: AppointmentTypeEligibilities | undefined) =>
  eligibilities
    ? (Object.keys(AppointmentTypeMapping) as LegacyVisitReason[]).filter(
        visitReason => eligibilities[visitReason],
      )
    : []

const STATES = {
  INIT: 0,
  BOOKING: 1,
  CANCELING: 2,
  CONFIRMING_CANCEL: 3,
  DELETING: 4,
} as const

interface PatientDetailAppointmentsProps {
  patient: { id: number; person: { id: number } }
}

/**
 * Design-refresh Appointments tab: the legacy container's BFF reads, cancel
 * flow, and booking modals verbatim, re-rendered with a segmented
 * Appointments/Cancellations toggle and SectionCard appointment cards.
 */
const PatientDetailAppointments = (props: PatientDetailAppointmentsProps) => {
  const {
    data: userVisitTypes,
    isPending: isLoadingVisitTypes,
    refetch: refetchVisitTypes,
  } = useGetCanUserBookAppointmentsV2(props.patient.id)
  const { data: featureSwitchData, isPending: isLoadingFeatureSwitches } = useFeatureSwitches()
  const isServerDrivenTypes =
    featureSwitchData?.featureSwitchesByName?.[SWITCH_SERVER_DRIVEN_APPOINTMENT_TYPES]?.active ===
    true
  const {
    data: appointmentTypeGroups,
    isPending: isLoadingTypeGroups,
    refetch: refetchTypeGroups,
  } = useAppointmentTypeGroups(props.patient.id)
  const { data: appointments, isPending: loadingAppointments } = useGetPatientAppointmentsInfo(
    props.patient.id,
  )
  const { data: appointmentCancelations, isPending: loadingAppointmentCancelations } =
    useGetPatientAppointmentCancelationsInfo(props.patient.id)

  const [tab, setTab] = useState<AppointmentTabTypes>('appointments')
  const [currentAppointment, setCurrentAppointment] = useState<
    Appointment | AppointmentCancelation | null
  >(null)
  const [state, setState] = useState<(typeof STATES)[keyof typeof STATES]>(STATES.INIT)
  const [anchorEl, setAnchorEl] = useState<Element | null>(null)
  const [isRefreshingTypes, setIsRefreshingTypes] = useState(false)

  // Cancelation state
  const [otherReasonForFirefly, setOtherReasonForFirefly] = useState<string>('')
  const [cancellationReasonIds, setCancellationReasonIds] = useState<Array<number>>([])
  const [otherReasonForPatient, setOtherReasonForPatient] = useState<string>('')
  const [selectedVisitType, setSelectedVisitType] = useState<VisitReason | null>(null)
  const [visitTypeTitle, setSelectedVisitTypeTitle] = useState<string>('')
  // Whether the selected group routes through symptom triage. Only the
  // server-driven wizard reads it; the legacy wizard infers routing from the
  // visit type.
  const [selectedGroupNeedsTriage, setSelectedGroupNeedsTriage] = useState(false)
  // The selected group's id, sent with the triage request so the resolver
  // prefers a type inside that group. Null on the legacy path.
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null)
  // Initial symptoms to prefill during reschedule
  const [initialSymptomIds, setInitialSymptomIds] = useState<number[] | undefined>(undefined)
  const [initialOtherSymptoms, setInitialOtherSymptoms] = useState<string[] | undefined>(undefined)
  const [initialSymptomsSelected, setInitialSymptomsSelected] = useState<SymptomItem[] | undefined>(
    undefined,
  )
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  // Closing any step of the booking or cancel flow discards what it collected.
  const returnToStart = () => {
    setState(STATES.INIT)
    setCurrentAppointment(null)
    setCancellationReasonIds([])
    setOtherReasonForPatient('')
    setOtherReasonForFirefly('')
    setInitialSymptomIds(undefined)
    setInitialOtherSymptoms(undefined)
    setInitialSymptomsSelected(undefined)
  }

  // Fetch details and prefill initial symptoms for reschedule (shared)
  const prefillSymptomsFromAppointment = useCallback(
    async (appointmentId: number) => {
      try {
        const detail = await queryClient.query({
          queryKey: appointment.retrieveQueryKey({ id: appointmentId }),
          queryFn: async () => {
            const response = await appointment.retrieve({ id: appointmentId })
            return response.data
          },
        })
        const apptSymptoms = detail?.appointment_symptoms || []
        const items = apptSymptoms
          .map(s => s.symptom)
          .filter(Boolean)
          .map(symptom => ({ id: symptom.id, label: symptom.label }))
        const ids = items.map(i => i.id)
        const other = detail?.other_symptoms?.trim() ? [detail.other_symptoms] : []
        setInitialSymptomsSelected(items)
        setInitialSymptomIds(ids)
        setInitialOtherSymptoms(other)
      } catch (e) {
        // Optional prefill; inform user and log
        logger.error('Failed to fetch appointment details', e)
        enqueueSnackbar(`Couldn't load previous concerns. You can still proceed without them.`, {
          variant: 'error',
        })
      }
    },
    [queryClient, enqueueSnackbar],
  )

  if (
    loadingAppointments ||
    isLoadingVisitTypes ||
    isLoadingFeatureSwitches ||
    isLoadingTypeGroups
  ) {
    return <Loader />
  }

  // Eligibility turns over mid-visit: joining the video checks the appointment out, which
  // establishes the member and changes which types are bookable. Both queries cache with
  // `staleTime: Infinity`, so an entry point into booking refetches and branches on what comes
  // back — the re-render lands after the branch has already read the previous value.
  const refreshBookableTypes = async () => {
    setIsRefreshingTypes(true)
    try {
      const [visitTypes, typeGroups] = await Promise.all([refetchVisitTypes(), refetchTypeGroups()])
      // A failed refetch resolves carrying the last data that succeeded, so booking degrades to
      // the cached menu rather than a dead button.
      return {
        eligibility: visitTypes.data,
        groups: typeGroups.data?.groups ?? [],
        ineligibilityRationale: typeGroups.data?.ineligibility_rationale ?? null,
      }
    } finally {
      setIsRefreshingTypes(false)
    }
  }

  const handleBookAppt = (visitReason: VisitReason | null, apptType: ApptTypes | '') => {
    setState(STATES.BOOKING)
    setSelectedVisitType(visitReason)
    setSelectedVisitTypeTitle(apptType)
    setSelectedGroupNeedsTriage(false)
    setSelectedGroupId(null)
  }

  // The response only carries types the member is eligible for, so a group can
  // arrive holding fewer types than its definition declares. The first
  // remaining type seeds the wizard; a triage group resolves the final type
  // from the member's symptoms.
  const handleBookGroup = (group: AppointmentTypeGroup) => {
    setState(STATES.BOOKING)
    setSelectedVisitType(group.appointment_types[0]?.unique_key ?? null)
    setSelectedVisitTypeTitle(group.title)
    setSelectedGroupNeedsTriage(group.needs_symptom_triage)
    setSelectedGroupId(group.id)
  }

  // Helper to derive and set visit type and title from an appointment's reason
  const applyVisitTypeFromAppointment = (
    appt: Appointment | AppointmentCancelation | null,
    groups: AppointmentTypeGroup[],
  ) => {
    if (!appt?.reason) return
    if (isServerDrivenTypes) {
      // A canceled appointment's reason is an appointment type's unique_key,
      // so its group supplies the title and the triage routing. A reason no
      // group carries (the member lost eligibility, or the row predates the
      // typed reasons) resets the selection, so the modal shows its
      // not-eligible state instead of a selection left over from an earlier
      // booking.
      const group = groups.find(candidate =>
        candidate.appointment_types.some(t => t.unique_key === appt.reason),
      )
      if (group) {
        setSelectedVisitType(appt.reason as ServerVisitReason)
        setSelectedVisitTypeTitle(group.title)
        setSelectedGroupNeedsTriage(group.needs_symptom_triage)
        setSelectedGroupId(group.id)
      } else {
        setSelectedVisitType(null)
        setSelectedVisitTypeTitle('Appointment')
        setSelectedGroupNeedsTriage(false)
        setSelectedGroupId(null)
      }
      return
    }
    const vr = reasonToVisitReason[appt.reason]
    if (vr) {
      setSelectedVisitType(vr)
      const apptType = VisitTypeMapping.find(([v]) => v === vr)?.[1] ?? ''
      setSelectedVisitTypeTitle(apptType)
    }
  }

  const cancelations = appointmentCancelations?.cancelation_events?.length ?? 0
  const eligibilities = userVisitTypes?.appointment_type_eligibilities
  // The server sets a rationale only when it found nothing bookable, so its presence is the
  // whole condition for blocking booking — the per-type booleans are not re-checked here, and
  // the message is rendered as received rather than mapped from `code`.
  const ineligibilityRationale = appointmentTypeGroups?.ineligibility_rationale ?? null

  const selectAppointmentForCancel = (appt: Appointment | AppointmentCancelation) => {
    setCurrentAppointment(appt)
    void prefillSymptomsFromAppointment(appt.id)
    setState(STATES.CANCELING)
  }

  const handleOpenBooking = async (anchor: HTMLElement) => {
    const refreshed = await refreshBookableTypes()
    const { eligibility, groups } = refreshed
    // The member may have lost eligibility since the tab mounted. Open nothing and
    // let the re-render leave the button disabled behind its tooltip.
    if (refreshed.ineligibilityRationale) {
      setAnchorEl(null)
      return
    }
    if (isServerDrivenTypes) {
      if (groups.length > 1) {
        setAnchorEl(anchor)
        return
      }
      const [onlyGroup] = groups
      if (onlyGroup) {
        handleBookGroup(onlyGroup)
      } else {
        handleBookAppt(null, 'Appointment')
      }
      setAnchorEl(null)
      return
    }
    const visitTypesAllowed = bookableVisitTypes(eligibility?.appointment_type_eligibilities)
    if (visitTypesAllowed.length > 1) {
      setAnchorEl(anchor)
      return
    }
    const [onlyVisitType] = visitTypesAllowed
    if (onlyVisitType !== undefined) {
      // Same title derivation as the legacy tab: visit reason → display
      // name → short label.
      handleBookAppt(
        onlyVisitType,
        VISIT_REASON_TO_LABEL[AppointmentTypeMapping[onlyVisitType]] as ApptTypes,
      )
    } else {
      handleBookAppt(null, 'Appointment')
    }
    setAnchorEl(null)
  }

  return (
    <Box sx={{ px: 3, pt: 1.5, pb: 5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
        }}
      >
        <SegmentedControl
          aria-label="Appointment list"
          options={[
            {
              value: 'appointments',
              label: `Appointments (${appointments?.appointments.length ?? 0})`,
            },
            { value: 'cancellations', label: `Cancellations (${cancelations})` },
          ]}
          value={tab}
          onChange={setTab}
        />
        {/* A disabled MUI button fires no pointer events, so the tooltip is anchored to a
            wrapper element instead. An empty title renders no tooltip at all, so an eligible
            member's button has no hover text. */}
        <Tooltip title={ineligibilityRationale?.message ?? ''}>
          <Box component="span" sx={{ flexShrink: 0 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              loading={isRefreshingTypes}
              // Start position keeps the label rendered; the default centered indicator hides it.
              loadingPosition="start"
              disabled={ineligibilityRationale !== null}
              onClick={e => {
                // Read the anchor before awaiting; the browser clears currentTarget once the
                // event finishes dispatching.
                void handleOpenBooking(e.currentTarget)
              }}
            >
              Book appointment
            </Button>
          </Box>
        </Tooltip>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          disableEnforceFocus
          disableRestoreFocus
          slotProps={{ list: { disablePadding: true } }}
        >
          {isServerDrivenTypes
            ? (appointmentTypeGroups?.groups ?? []).map(group => (
                <MenuItem
                  key={group.id}
                  onClick={() => {
                    handleBookGroup(group)
                    setAnchorEl(null)
                  }}
                >
                  {group.title}
                </MenuItem>
              ))
            : VisitTypeMapping.map(([visitReason, apptType]) =>
                eligibilities?.[visitReason] ? (
                  <MenuItem
                    key={visitReason}
                    onClick={() => {
                      handleBookAppt(visitReason, apptType)
                      setAnchorEl(null)
                    }}
                  >
                    {apptType}
                  </MenuItem>
                ) : null,
              )}
        </Menu>
      </Box>

      {tab === 'appointments' ? (
        loadingAppointments || !appointments?.appointments ? (
          <Loader />
        ) : appointments.appointments.length ? (
          appointments.appointments.map(appt => (
            <AppointmentCard
              key={appt.id}
              appointment={appt}
              patientId={props.patient.id}
              selectAppointmentForCancel={selectAppointmentForCancel}
            />
          ))
        ) : (
          <EmptyState icon={<EventIcon />} title="No appointments" />
        )
      ) : loadingAppointmentCancelations || !appointmentCancelations ? (
        <Loader />
      ) : appointmentCancelations.cancelation_events.length ? (
        appointmentCancelations.cancelation_events.map(appt => (
          <AppointmentCard
            key={appt.id}
            appointment={appt}
            patientId={props.patient.id}
            selectAppointmentForCancel={selectAppointmentForCancel}
            isCanceled
          />
        ))
      ) : (
        <EmptyState icon={<EventBusyIcon />} title="No cancellations" />
      )}

      {state === STATES.BOOKING ? (
        <BookAppointmentModal
          personId={props.patient.person.id}
          patientId={props.patient.id}
          onClose={returnToStart}
          visitType={selectedVisitType}
          visitTypeTitle={visitTypeTitle}
          needsSymptomTriage={selectedGroupNeedsTriage}
          appointmentTypeGroupId={selectedGroupId ?? undefined}
          initialSymptomIds={initialSymptomIds}
          initialOtherSymptoms={initialOtherSymptoms}
          initialSymptomsSelected={initialSymptomsSelected}
        />
      ) : null}
      {state === STATES.CANCELING && currentAppointment ? (
        <CancelAppointmentModal
          cancellationReasonIds={cancellationReasonIds}
          setCancellationReasonIds={setCancellationReasonIds}
          otherReasonForPatient={otherReasonForPatient}
          otherReasonForFirefly={otherReasonForFirefly}
          setOtherReasonForPatient={setOtherReasonForPatient}
          setOtherReasonForFirefly={setOtherReasonForFirefly}
          onClose={success => {
            if (success) {
              applyVisitTypeFromAppointment(currentAppointment, appointmentTypeGroups?.groups ?? [])
              setState(STATES.CONFIRMING_CANCEL)
            } else {
              returnToStart()
            }
          }}
        />
      ) : null}
      {state === STATES.CONFIRMING_CANCEL && currentAppointment ? (
        <ConfirmCancelApptModal
          appointment={{ id: currentAppointment.id, patient: props.patient.id }}
          cancellationReasonIds={cancellationReasonIds}
          otherReasonForPatient={otherReasonForPatient}
          otherReasonForFirefly={otherReasonForFirefly}
          onClose={continueToBooking => {
            if (!continueToBooking) {
              returnToStart()
              return
            }
            void refreshBookableTypes().then(({ groups }) => {
              applyVisitTypeFromAppointment(currentAppointment, groups)
              setState(STATES.BOOKING)
            })
          }}
        />
      ) : null}
    </Box>
  )
}

export default PatientDetailAppointments
